import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { ToastProvider, useToast } from './utils/useToast';
import { PanelMain, PanelRoot } from './PanelComerciales.styles';
import StickyHeader from './sections/StickyHeader/StickyHeader';
import QuickActions, { QuickActionId } from './sections/QuickActions/QuickActions';
import TeamsTable from './sections/TeamsTable/TeamsTable';
import CommercialsTable from './sections/CommercialsTable/CommercialsTable';
import VipTable from './sections/VipTable/VipTable';
import DuplicatesSection from './sections/DuplicatesSection/DuplicatesSection';
import QueueSection from './sections/QueueSection/QueueSection';
import { PANEL_PAGE_SIZE } from './sections/Pagination/Pagination';
import NotificationsTable from './sections/NotificationsTable/NotificationsTable';
import AuditLog from './sections/AuditLog/AuditLog';
import CreateTeamDrawer from './drawers/CreateTeamDrawer';
import BlockCommercialDrawer from './drawers/BlockCommercialDrawer';
import {
    Commercial,
    Team,
    VipAccount,
    sameSpec,
} from './PanelComerciales.types';
import {
    selectActiveTeamNames,
    selectActivityLoading,
    selectActivityPage,
    selectCommercials,
    selectDealsStatus,
    selectProducts,
    selectFlowTypes,
    selectNotificationsLoading,
    selectNotificationsPage,
    selectPanelLoading,
    selectPanelMeta,
    selectTeams,
    selectVips,
} from 'src/redux/panel_comerciales/panel_comerciales.selectors';
import {
    appendAuditLog,
    removeVip,
    setDuplicatesSearch,
    upsertCommercial,
} from 'src/redux/panel_comerciales/panel_comerciales.slice';
import {
    assignFromQueueBatchThunk,
    assignFromQueueThunk,
    assignVipByEmailThunk,
    createTeamThunk,
    loadActivity,
    loadAssignmentQueue,
    loadDealDuplicates,
    loadNotifications,
    loadSnapshot,
    mergeDealsThunk,
    saveSellerConfigThunk,
    saveVipOverrideThunk,
    updateTeamThunk,
} from 'src/redux/panel_comerciales/thunks';
import {
    DealMergePayload,
    DuplicatesSearch,
    QueueItem,
    SellerConfigPatch,
    VipOverridePatch,
} from 'src/redux/panel_comerciales/panel_comerciales.types';
import { buildAuditEntry } from './utils/audit';
import { PERMANENT_BLOCK_UNTIL } from './utils/blockSentinel';
import { parseCurrencyToNumber } from './utils/currency';
import type { AppDispatch } from 'src/redux/store';

/**
 * Mapeo de fallback `status` UI → `state` backend cuando el drawer NO trae
 * un `statusReason` explícito (caso legacy). Para los flujos nuevos el drawer
 * de edición rellena `statusReason` con el motivo elegido y este mapeo no se
 * usa; los handlers prefieren `next.statusReason` cuando está disponible.
 */
const STATE_BY_STATUS: Record<Commercial['status'], SellerConfigPatch['state']> = {
    Activo: 'activo',
    Inactivo: 'vacaciones',
    Bloqueado: 'inactivo',
};

/**
 * Aplica al patch los campos derivados del cambio de status (state,
 * since/until/reason). Se extrae para evitar que `buildCommercialPatch`
 * acumule complejidad cognitiva con las ramas Activo / Inactivo / Bloqueado.
 */
const applyStatusChangeToPatch = (
    patch: SellerConfigPatch,
    next: Commercial,
): void => {
    if (next.status === 'Activo') {
        patch.state = 'activo';
        patch.state_since = null;
        patch.state_until = null;
        patch.state_reason = null;
        return;
    }
    // Inactivo (temporal) / Bloqueado (definitivo). El drawer rellena
    // `statusReason` con el motivo elegido; cae al mapeo si no está.
    patch.state = (next.statusReason as SellerConfigPatch['state'])
        ?? STATE_BY_STATUS[next.status];
    patch.state_since = next.statusSince ?? null;
    patch.state_until = next.status === 'Bloqueado'
        ? PERMANENT_BLOCK_UNTIL  // bloqueo definitivo → centinela 9999-12-31
        : (next.tempBlockUntil ?? null);
    patch.state_reason = next.absence && next.absence !== '-' ? next.absence : null;
};

const parseGoalAmount = (raw: string): number | undefined => {
    const numericGoal = parseCurrencyToNumber(raw);
    return numericGoal !== undefined && numericGoal > 0 ? numericGoal : undefined;
};

/**
 * Switches booleanos del panel. Los dos siguen el mismo patrón (comparar el
 * flag, emitir su campo del patch y una línea de summary), así que se declaran
 * como datos en vez de repetir el bloque: "Campañas" gobierna la rotación de
 * marketing y "Renovación" el flujo de renovación — ninguno se reparte por
 * porcentaje, por eso viven fuera del modal de distribución.
 */
const BOOLEAN_TOGGLES = [
    {
        viewKey: 'marketingEnabled',
        patchKey: 'marketing_enabled',
        label: 'campañas',
        onLabel: 'activadas',
        offLabel: 'desactivadas',
    },
    {
        viewKey: 'renovationEnabled',
        patchKey: 'renovation_enabled',
        label: 'renovación',
        onLabel: 'activada',
        offLabel: 'desactivada',
    },
] as const;

const applyToggleChanges = (
    prev: Commercial,
    next: Commercial,
    patch: SellerConfigPatch,
    summaryParts: string[],
): void => {
    BOOLEAN_TOGGLES.forEach(({ viewKey, patchKey, label, onLabel, offLabel }) => {
        const value = Boolean(next[viewKey]);
        if (value === Boolean(prev[viewKey])) return;
        patch[patchKey] = value;
        summaryParts.push(`${label} ${value ? onLabel : offLabel}`);
    });
};

export const buildCommercialPatch = (
    prev: Commercial,
    next: Commercial,
    period: string,
): { patch: SellerConfigPatch; summaryParts: string[] } => {
    const patch: SellerConfigPatch = { period };
    const summaryParts: string[] = [];

    if (next.capacity !== prev.capacity) {
        patch.capacity_pct = next.capacity;
        summaryParts.push(`capacidad ${prev.capacity}% → ${next.capacity}%`);
    }
    const nextProducts = next.products ?? next.specialty ?? {};
    const prevProducts = prev.products ?? prev.specialty ?? {};
    if (!sameSpec(nextProducts, prevProducts)) {
        // EJE PRODUCTO: claves del dict = product_code. El DTO de api-crm lo lee
        // como `products`; `distribution` se mantiene como alias legacy (R1).
        patch.products = nextProducts;
        patch.distribution = nextProducts;
        summaryParts.push('distribución por producto actualizada');
    }
    const nextFlows = next.flows ?? {};
    const prevFlows = prev.flows ?? {};
    if (!sameSpec(nextFlows, prevFlows)) {
        // EJE FLUJO: claves del dict = pipeline_key. El DTO de api-crm lo lee como `flows`.
        patch.flows = nextFlows;
        summaryParts.push('distribución por flujo actualizada');
    }
    if (next.goal !== prev.goal) {
        const numericGoal = parseGoalAmount(next.goal);
        if (numericGoal !== undefined) {
            patch.goals = [{ type_goal: 'revenue', goal: numericGoal }];
            summaryParts.push(`meta ${prev.goal} → ${next.goal}`);
        }
    }
    if (next.team !== prev.team) {
        patch.team = next.team;
        summaryParts.push(`equipo ${prev.team} → ${next.team}`);
    }
    if (next.status !== prev.status) {
        applyStatusChangeToPatch(patch, next);
        summaryParts.push(`estado ${prev.status} → ${next.status}`);
    }
    if (typeof next.notes === 'string' && next.notes !== prev.notes) {
        patch.notes = next.notes;
    }
    applyToggleChanges(prev, next, patch, summaryParts);

    return { patch, summaryParts };
};

const PanelComercialesInner: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();

    const meta = useSelector(selectPanelMeta);
    const products = useSelector(selectProducts);
    const flowTypes = useSelector(selectFlowTypes);
    const teams = useSelector(selectTeams);
    const commercials = useSelector(selectCommercials);
    const vips = useSelector(selectVips);
    const notificationsPage = useSelector(selectNotificationsPage);
    const notificationsLoading = useSelector(selectNotificationsLoading);
    const activityPage = useSelector(selectActivityPage);
    const activityLoading = useSelector(selectActivityLoading);
    const teamOptions = useSelector(selectActiveTeamNames);
    const loading = useSelector(selectPanelLoading);
    const dealsStatus = useSelector(selectDealsStatus);

    const [drawer, setDrawer] = useState<QuickActionId | null>(null);

    useEffect(() => {
        dispatch(loadSnapshot());
        dispatch(loadDealDuplicates({ limit: PANEL_PAGE_SIZE, offset: 0 }));
        dispatch(loadAssignmentQueue({ limit: PANEL_PAGE_SIZE, offset: 0 }));
        dispatch(loadNotifications({ limit: PANEL_PAGE_SIZE, offset: 0 }));
        dispatch(loadActivity({ limit: PANEL_PAGE_SIZE, offset: 0 }));
    }, [dispatch]);

    const teamNames = useMemo(() => teams.map((t) => t.name), [teams]);

    // Líder de equipo: solo comerciales ACTIVOS son elegibles en los
    // desplegables (el backend sigue recibiendo el nombre como string).
    const leaderOptions = useMemo(
        () =>
            commercials
                .filter((c) => c.active)
                .map((c) => c.name)
                .sort((a, b) => a.localeCompare(b, 'es')),
        [commercials],
    );

    const recordAudit = useCallback(
        (action: string, detail: string) => {
            dispatch(appendAuditLog(buildAuditEntry(action, detail)));
        },
        [dispatch],
    );

    const handleQuickAction = (action: QuickActionId) => setDrawer(action);
    const closeDrawer = () => setDrawer(null);

    const handleTeamUpsert = (next: Team, prevName?: string) => {
        const isEdit = Boolean(prevName);
        if (isEdit) {
            if (next.id === undefined) {
                showToast({ message: 'No se pudo identificar el equipo', severity: 'error' });
                return;
            }
            dispatch(
                updateTeamThunk({
                    teamId: next.id,
                    patch: {
                        name: next.name,
                        leader: next.leader,
                        goal: parseCurrencyToNumber(next.goal),
                        active: next.active,
                    },
                }),
            );
            recordAudit('Actualizó equipo', next.name);
            showToast({ message: 'Equipo actualizado', severity: 'success' });
            return;
        }
        dispatch(
            createTeamThunk({
                payload: {
                    name: next.name,
                    leader: next.leader,
                    goal: parseCurrencyToNumber(next.goal),
                    active: next.active,
                },
            }),
        );
        recordAudit('Creó equipo', `${next.name} (líder ${next.leader}, meta ${next.goal})`);
        showToast({ message: 'Equipo creado', severity: 'success' });
    };

    const handleTeamDeactivate = (team: Team) => {
        if (team.id === undefined) {
            showToast({ message: 'No se pudo identificar el equipo', severity: 'error' });
            return;
        }
        dispatch(updateTeamThunk({ teamId: team.id, patch: { active: false } }));
        recordAudit('Desactivó equipo', team.name);
        showToast({
            message: `Equipo ${team.name} desactivado · integrantes liberados`,
            severity: 'info',
        });
    };

    const handleCommercialUpdate = async (next: Commercial, prev: Commercial) => {
        dispatch(upsertCommercial(next));

        const { patch, summaryParts } = buildCommercialPatch(prev, next, meta.period);

        if (Object.keys(patch).length > 1) {
            try {
                // El toast de éxito solo si el PUT resolvió; antes se mostraba
                // "Cambios guardados" incondicional y un 500 (ej. metas) pasaba
                // como éxito hasta el siguiente refresh.
                await dispatch(
                    saveSellerConfigThunk({ sellerId: next.id, patch }),
                ).unwrap();
            } catch (err) {
                // Revierte el update optimista recargando el snapshot real.
                dispatch(loadSnapshot());
                // unwrap() lanza un SerializedError (objeto plano), no un Error.
                const message = (err as { message?: string })?.message;
                const detail = message ? ` (${message})` : '';
                showToast({
                    message: `No se pudieron guardar los cambios de ${next.name}${detail}`,
                    severity: 'error',
                });
                return;
            }
        }

        if (summaryParts.length > 0) {
            recordAudit(
                'Actualizó comercial',
                `${next.name}: ${summaryParts.join(', ')}`,
            );
            showToast({ message: 'Cambios guardados', severity: 'success' });
        }
    };

    // --- Sección "Estado de deals" (Reglas de Marketing jul-2026) ---

    const handleLoadDuplicatesPage = useCallback(
        (offset: number) => {
            dispatch(loadDealDuplicates({
                limit: PANEL_PAGE_SIZE,
                offset,
                email: dealsStatus.duplicatesSearch.email || undefined,
                owner: dealsStatus.duplicatesSearch.owner || undefined,
            }));
        },
        [dispatch, dealsStatus.duplicatesSearch],
    );

    const handleDuplicatesSearch = useCallback(
        (search: DuplicatesSearch) => {
            // Nueva búsqueda: guardar filtro y volver a la primera página.
            dispatch(setDuplicatesSearch(search));
            dispatch(loadDealDuplicates({
                limit: PANEL_PAGE_SIZE,
                offset: 0,
                email: search.email || undefined,
                owner: search.owner || undefined,
            }));
        },
        [dispatch],
    );

    const handleLoadQueuePage = useCallback(
        (offset: number) => {
            dispatch(loadAssignmentQueue({ limit: PANEL_PAGE_SIZE, offset }));
        },
        [dispatch],
    );

    const handleLoadNotifPage = useCallback(
        (offset: number) => {
            dispatch(loadNotifications({ limit: PANEL_PAGE_SIZE, offset }));
        },
        [dispatch],
    );

    const handleLoadActivityPage = useCallback(
        (offset: number) => {
            dispatch(loadActivity({ limit: PANEL_PAGE_SIZE, offset }));
        },
        [dispatch],
    );

    const handleMergeDeals = async (payload: DealMergePayload) => {
        try {
            await dispatch(mergeDealsThunk({ payload })).unwrap();
            recordAudit(
                'Fusionó deals duplicados',
                `deal ${payload.merged_deal_id} → deal ${payload.surviving_deal_id}`,
            );
            showToast({ message: 'Deals fusionados', severity: 'success' });
        } catch (err) {
            const message = (err as { message?: string })?.message ?? '';
            showToast({
                message: `No se pudo fusionar (${message})`,
                severity: 'error',
            });
        }
    };

    const handleAssignFromQueue = async (item: QueueItem, sellerId: number) => {
        try {
            await dispatch(
                assignFromQueueThunk({
                    payload: { users_id: item.usersId, seller_id: sellerId },
                    queuePage: { limit: PANEL_PAGE_SIZE, offset: dealsStatus.queue.offset },
                }),
            ).unwrap();
            const sellerName = commercials.find((c) => c.id === sellerId)?.name ?? '';
            recordAudit(
                'Asignó desde la cola',
                `${item.clientName ?? item.usersId} → ${sellerName}`,
            );
            showToast({ message: 'Deal creado y asignado', severity: 'success' });
        } catch (err) {
            const message = (err as { message?: string })?.message ?? '';
            showToast({
                message: `No se pudo asignar (${message})`,
                severity: 'error',
            });
        }
    };

    const handleAssignBatch = async (usersIds: number[], sellerId: number) => {
        try {
            const result = await dispatch(
                assignFromQueueBatchThunk({
                    payload: { users_ids: usersIds, seller_id: sellerId },
                    queuePage: { limit: PANEL_PAGE_SIZE, offset: dealsStatus.queue.offset },
                }),
            ).unwrap();
            const sellerName = commercials.find((c) => c.id === sellerId)?.name ?? '';
            recordAudit(
                'Asignó deals en lote',
                `${result.assignedCount} deal(s) → ${sellerName}` +
                    (result.rejectedCount ? `, ${result.rejectedCount} rechazado(s)` : ''),
            );
            // Resumen de resultados parciales.
            const severity = result.rejectedCount === 0 ? 'success' : 'info';
            showToast({
                message:
                    `${result.assignedCount} asignado(s)` +
                    (result.rejectedCount
                        ? `, ${result.rejectedCount} rechazado(s) (VIP o elegibilidad)`
                        : ''),
                severity,
            });
        } catch (err) {
            const message = (err as { message?: string })?.message ?? '';
            showToast({
                message: `No se pudo asignar el lote (${message})`,
                severity: 'error',
            });
        }
    };

    const handleVipAssign = (payload: {
        clientEmail: string;
        sellerId: number;
        validity?: string;
    }) => {
        dispatch(
            assignVipByEmailThunk({
                payload: {
                    client_email: payload.clientEmail,
                    seller_id: payload.sellerId,
                    validity: payload.validity,
                },
            }),
        );
        const ownerName = commercials.find((c) => c.id === payload.sellerId)?.name ?? '';
        recordAudit('Asignó cuenta VIP', `${payload.clientEmail} → ${ownerName}`);
        showToast({ message: 'VIP asignado', severity: 'success' });
    };

    const handleVipEdit = (
        vip: VipAccount,
        change: { sellerId: number; validity?: string },
    ) => {
        const patch: VipOverridePatch = {
            seller_id: change.sellerId,
            action: 'assign',
            validity: change.validity,
            client_email: vip.email,
            client_name: vip.client,
        };
        dispatch(saveVipOverrideThunk({ clientId: vip.id, patch }));
        const ownerName = commercials.find((c) => c.id === change.sellerId)?.name ?? '';
        recordAudit('Editó cuenta VIP', `${vip.client} → ${ownerName}`);
        showToast({ message: 'VIP actualizado', severity: 'success' });
    };

    const handleVipRevoke = (vip: VipAccount) => {
        dispatch(removeVip(vip.id));
        const patch: VipOverridePatch = {
            action: 'revoke',
        };
        dispatch(saveVipOverrideThunk({ clientId: vip.id, patch }));
        recordAudit('Revocó cuenta VIP', `${vip.client} (${vip.owner})`);
        showToast({ message: 'VIP revocado', severity: 'info' });
    };

    const handleBlockConfirm = (sellerId: number, patch: SellerConfigPatch, label: string) => {
        const seller = commercials.find((c) => c.id === sellerId);
        if (!seller) return;
        dispatch(
            upsertCommercial({
                ...seller,
                status: 'Bloqueado',
                active: false,
                absence: `${patch.state ?? ''} ${patch.state_since ?? ''} → ${patch.state_until ?? ''}`.trim(),
                tempBlockUntil: patch.state_until ?? undefined,
            }),
        );
        dispatch(saveSellerConfigThunk({ sellerId, patch }));
        recordAudit('Bloqueó comercial', label);
        showToast({ message: 'Bloqueo registrado', severity: 'warning' });
    };

    return (
        <PanelRoot>
            <StickyHeader />
            {loading && <LinearProgress sx={{ height: 2 }} />}
            <PanelMain>
                <QuickActions onAction={handleQuickAction} />

                <TeamsTable
                    teams={teams}
                    leaderOptions={leaderOptions}
                    onTeamUpsert={handleTeamUpsert}
                    onTeamDeactivate={handleTeamDeactivate}
                />

                <CommercialsTable
                    commercials={commercials}
                    teamOptions={teamOptions}
                    products={products}
                    flowTypes={flowTypes}
                    onCommercialUpdate={handleCommercialUpdate}
                />

                <VipTable
                    vips={vips}
                    commercials={commercials}
                    onAssign={handleVipAssign}
                    onEdit={handleVipEdit}
                    onRevoke={handleVipRevoke}
                />

                <QueueSection
                    page={dealsStatus.queue}
                    loading={dealsStatus.queueLoading}
                    commercials={commercials}
                    actionLoading={dealsStatus.actionLoading}
                    onLoadPage={handleLoadQueuePage}
                    onAssign={handleAssignFromQueue}
                    onAssignBatch={handleAssignBatch}
                />

                <DuplicatesSection
                    page={dealsStatus.duplicates}
                    loading={dealsStatus.duplicatesLoading}
                    search={dealsStatus.duplicatesSearch}
                    commercials={commercials}
                    actionLoading={dealsStatus.actionLoading}
                    onSearch={handleDuplicatesSearch}
                    onLoadPage={handleLoadDuplicatesPage}
                    onMerge={handleMergeDeals}
                />

                <NotificationsTable
                    page={notificationsPage}
                    loading={notificationsLoading}
                    onLoadPage={handleLoadNotifPage}
                />

                <AuditLog
                    page={activityPage}
                    loading={activityLoading}
                    onLoadPage={handleLoadActivityPage}
                />
            </PanelMain>

            <CreateTeamDrawer
                open={drawer === 'create-team'}
                onClose={closeDrawer}
                existingNames={teamNames}
                leaderOptions={leaderOptions}
                onSave={(t) => handleTeamUpsert(t)}
            />
            <BlockCommercialDrawer
                open={drawer === 'block-commercial'}
                onClose={closeDrawer}
                commercials={commercials}
                period={meta.period}
                onConfirm={handleBlockConfirm}
            />
        </PanelRoot>
    );
};

const PanelComerciales: React.FC = () => (
    <ToastProvider>
        <PanelComercialesInner />
    </ToastProvider>
);

export default PanelComerciales;
